import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { SelectField, InputField } from "@/components/ui/FormFields";
import { Modal } from "@/components/ui/Modal";
import {
  getYashFraudQueue,
  processYashFraud,
  type YashFraudFlag,
} from "@/api/admin";

export default function YashFraudLab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<"" | "pending" | "fraud_held">("pending");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-yash-fraud", status],
    queryFn: () => getYashFraudQueue({ status: status || undefined }),
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["admin-yash-fraud"] });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Yash Fraud Queue"
        subtitle="Review flagged Yash transactions. Clear false positives or reverse fraudulent awards."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Yash Fraud" },
        ]}
      />

      <div className="max-w-xs">
        <SelectField
          label="Filter"
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as "" | "pending" | "fraud_held")
          }
          options={[
            { value: "", label: "All open" },
            { value: "pending", label: "Pending" },
            { value: "fraud_held", label: "Fraud held" },
          ]}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.items.length === 0 ? (
        <p className="text-sm text-gray-500">No flagged transactions.</p>
      ) : (
        <ul className="space-y-2">
          {data.data.items.map((f) => (
            <FraudRow key={f.id} flag={f} onChange={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FraudRow({
  flag,
  onChange,
}: {
  flag: YashFraudFlag;
  onChange: () => void;
}) {
  const [modal, setModal] = useState<"clear" | "confirm_fraud" | null>(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  async function handleProcess(action: "clear" | "confirm_fraud") {
    setBusy(true);
    try {
      await processYashFraud(flag.id, action, notes || undefined);
      onChange();
      setModal(null);
      setNotes("");
    } finally {
      setBusy(false);
    }
  }

  const canAct = flag.status === "pending";
  return (
    <li className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="gray">{flag.actionType}</Badge>
          <Badge
            variant={
              flag.status === "pending"
                ? "yellow"
                : flag.status === "cleared"
                  ? "green"
                  : "red"
            }
          >
            {flag.status}
          </Badge>
          <span className="font-semibold text-gray-900">
            {flag.yash} yash
          </span>
        </div>
        <p className="text-sm text-gray-700 mt-1">{flag.fraudReason}</p>
        <div className="text-xs text-gray-500 mt-1">
          {flag.user?.name ?? flag.userId.slice(0, 8)} ·{" "}
          {new Date(flag.createdAt).toLocaleString()}
        </div>
      </div>
      {canAct && (
        <div className="flex gap-1 shrink-0">
          <Button size="sm" onClick={() => setModal("clear")}>
            Clear
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setModal("confirm_fraud")}
          >
            Confirm fraud
          </Button>
        </div>
      )}

      <Modal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        title={
          modal === "clear" ? "Clear fraud flag" : "Confirm fraud"
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button
              variant={modal === "confirm_fraud" ? "danger" : "primary"}
              onClick={() => modal && handleProcess(modal)}
              isLoading={busy}
            >
              {modal === "clear" ? "Clear" : "Confirm fraud"}
            </Button>
          </>
        }
      >
        <InputField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Modal>
    </li>
  );
}
